import { type SiteId } from '@solinkcorp/api-types';
import { getAccessibleAddress } from '@solinkcorp/utils';
import { ALL_LOCATIONS } from '@solinkcorp/vd-common';
import { CallhomeApi, DeviceProxyApi, VmsApi } from 'clients';
import {
  type CallhomeAlarmConfig,
  type CallhomeAlarmState,
  type ScheduledActivityEntity,
} from 'clients/CallhomeApiTypes';
import { AppDispatcher } from 'dispatcher';
import { useCallback, useState } from 'react';
import useDevicesFromSite from 'src/hooks/devices/useDevicesFromSite';
import { useDevicesAreLocal } from 'src/hooks/useDeviceIsLocal';
import useIsUserStandard from 'src/hooks/useIsUserStandard';
import useSWR, { useSWRConfig } from 'swr';
import { isAlarmUntilUnixValid } from 'utils/AlarmUtil';
import { type VideoDiscoverySite } from 'utils/types';
import { NO_TEMP_ARM, NO_TEMP_DISARM, UPDATE_LOCATION_ALARM_CONFIG } from 'vd-constants';
import { useMergedSchedule } from './useMergedSchedule';

function mergeAlarmConfig(
  alarmConfig: CallhomeAlarmConfig,
  update: Partial<CallhomeAlarmConfig>,
): CallhomeAlarmConfig {
  return {
    ...alarmConfig,
    ...update,
    operationSettings: mergeOperationSettings(
      alarmConfig.operationSettings,
      update.operationSettings,
    ),
  } as CallhomeAlarmConfig;
}

function mergeOperationSettings(
  current: CallhomeAlarmConfig['operationSettings'],
  update: CallhomeAlarmConfig['operationSettings'],
): CallhomeAlarmConfig['operationSettings'] {
  if (!update) {
    return current;
  }
  // A new mode drops the previous mode's expiry until the server returns the new one.
  const { modeValidUntil: _modeValidUntil, ...withoutExpiry } = current ?? {};
  return { ...(update.alarmMonitoringMode !== undefined ? withoutExpiry : current), ...update };
}

function normalizeAlarmUntilUpdate(
  update: Partial<CallhomeAlarmConfig>,
  timezone?: string,
): Partial<CallhomeAlarmConfig> {
  const normalizedUpdate = { ...update };
  if (!timezone) {
    return normalizedUpdate;
  }

  const { armUntil, disarmUntil } = normalizedUpdate;
  if (armUntil !== undefined) {
    if (armUntil && isAlarmUntilUnixValid(armUntil, Date.now(), timezone)) {
      normalizedUpdate.disarmUntil = NO_TEMP_DISARM;
    } else {
      normalizedUpdate.armUntil = NO_TEMP_ARM;
    }
  }
  if (disarmUntil !== undefined) {
    if (disarmUntil && isAlarmUntilUnixValid(disarmUntil, Date.now(), timezone)) {
      normalizedUpdate.armUntil = NO_TEMP_ARM;
    } else {
      normalizedUpdate.disarmUntil = NO_TEMP_DISARM;
    }
  }

  return normalizedUpdate;
}

// Unique key for SWR cache.
const ALARM_CONFIG_KEY = 'callhome-alarm-config';
const REFRESH_INTERVAL = 1000 * 10; // 10 seconds in milliseconds.

export interface useAlarmConfigOptions {
  autoRefresh?: boolean;
}

export const getAlarmConfigKey = (locationId: string) => [ALARM_CONFIG_KEY, locationId];

/**
 * Returns a cached alarm configuration for the given location,
 * and a function that can be used to update the alarm configuration.  The update
 * function will update the alarm configuration in the cache, in the location store,
 * and will ask the device to do a check-in. If the location provided is possibly
 * undefined, then the resultant CallhomeAlarmConfig could be undefined also.
 *
 * @returns an {alarmConfig, updateAlarmConfig, alarmConfigError, busy, locationWithMergedSchedule} object.
 */
export function useAlarmConfigForLocation<
  L extends VideoDiscoverySite | undefined,
  R = L extends undefined ? undefined : CallhomeAlarmConfig,
>(
  location: L,
  disableRefresh = false,
  disabled = false,
  activeExceptions?: ScheduledActivityEntity[],
): {
  alarmConfig: R;
  updateAlarmConfig: (alarmConfiguration: Partial<CallhomeAlarmConfig>) => Promise<void>;
  alarmConfigError: Error;
  busy: boolean;
  locationWithMergedSchedule: L;
} {
  // Use the new hook to merge schedule with active exceptions
  const { locationWithMergedSchedule, mergedScheduleLoading } = useMergedSchedule(location, {
    activeExceptions,
  });

  // Fetch the AlarmConfig from the SWR cache.  This will automatically re-fetch
  // the alarm config every REFRESH_INTERVAL, or if the window regains focus after
  // losing it, or if we lose our connection and regain it.  Provided the location
  // has an `alarmConfig`, this will be used as the initial state on mount.
  const { data: alarmConfig, error } = useSWR(
    () =>
      !disabled && location && location.id !== ALL_LOCATIONS
        ? getAlarmConfigKey(location.id)
        : null,
    /** Alarm config has to be requested from the location endpoint as it's accessible
     * for both standard and admin users.
     */
    ([, locationId]) => CallhomeApi().getAlarmConfigFromLocation({ siteId: locationId as SiteId }),
    {
      fallbackData: location?.alarmConfig,
      refreshInterval: disableRefresh ? 0 : REFRESH_INTERVAL,
      onSuccess: (alarmConfig) => {
        if (alarmConfig && location) {
          // Keep the location store's copy of the AlarmConfig up to date.
          updateLocationStore(location.id, alarmConfig);
        }
      },
    },
  );

  const { updateAlarmConfig, busy } = useUpdateAlarmConfiguration(location);

  return {
    alarmConfig: (alarmConfig || location?.alarmConfig) as R,
    updateAlarmConfig,
    alarmConfigError: error,
    busy: busy || mergedScheduleLoading,
    locationWithMergedSchedule,
  };
}

/**
 * Returns a function that can be used to update the alarm configuration.
 */
export function useUpdateAlarmConfiguration(location: VideoDiscoverySite | undefined) {
  const { mutate } = useSWRConfig();
  const { timezone, id: siteId } = location || {};
  const [busy, setBusy] = useState(false);
  const isStandardUser = useIsUserStandard();

  const { deviceIds, devices } = useDevicesFromSite(siteId ?? '');
  const localDevices = useDevicesAreLocal(deviceIds);

  const fallbackConfig = location?.alarmConfig;

  // Callback to update the alarm config.
  const updateAlarmConfig = useCallback(
    async (update: Partial<CallhomeAlarmConfig>) => {
      if (!siteId) {
        throw new Error('Need location to update alarm config.');
      }

      setBusy(true);
      const normalizedUpdate = normalizeAlarmUntilUpdate(update, timezone);

      try {
        await mutate(
          getAlarmConfigKey(siteId),
          async (alarmConfig: CallhomeAlarmConfig | undefined) => {
            const currentAlarmConfig = alarmConfig ?? fallbackConfig;
            if (!currentAlarmConfig) {
              throw new Error('Need alarm configuration to update alarm config.');
            }

            let updatedAlarmConfig;
            const propsNotPartOfAlarmState = Object.keys(normalizedUpdate).filter(
              (prop) => !['state', 'armUntil', 'disarmUntil'].includes(prop),
            );
            if (propsNotPartOfAlarmState.length === 0) {
              updatedAlarmConfig = await CallhomeApi().updateAlarmState(
                siteId,
                normalizedUpdate as CallhomeAlarmState,
              );
            } else {
              updatedAlarmConfig = await CallhomeApi().updateAlarmConfig(siteId, normalizedUpdate);
            }

            if (devices) {
              for (const device of devices) {
                const isLocal = localDevices[device.id];
                const accessibleAddress = getAccessibleAddress(device, isLocal);

                if (accessibleAddress) {
                  try {
                    if (isStandardUser) {
                      await DeviceProxyApi().lightCheckin(device.id);
                    } else {
                      await VmsApi(accessibleAddress).lightCheckin();
                    }
                  } catch (error: any) {
                    // Device check-in is best-effort; the config has already been
                    // saved. Log instead of toasting so the caller's success/error
                    // toast remains the single source of truth for the user.
                    console.error('Failed to propagate alarm config to device', device.id, error);
                  }
                }
              }
            }

            updateLocationStore(siteId, updatedAlarmConfig);

            return updatedAlarmConfig;
          },
          {
            optimisticData: (currentAlarmConfig?: CallhomeAlarmConfig) =>
              mergeAlarmConfig(
                currentAlarmConfig ?? fallbackConfig ?? ({} as CallhomeAlarmConfig),
                normalizedUpdate,
              ),
            rollbackOnError: true,
            populateCache: true,
            revalidate: false,
          },
        );
      } finally {
        setBusy(false);
      }
    },
    [fallbackConfig, isStandardUser, timezone, devices, siteId, mutate, localDevices],
  );

  return { updateAlarmConfig, busy };
}

// Let the LocationStore know that the alarm configuration has changed.
function updateLocationStore(siteId: string, alarmConfig: CallhomeAlarmConfig) {
  AppDispatcher.dispatch(UPDATE_LOCATION_ALARM_CONFIG, { locationId: siteId, alarmConfig });
}
